import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useSettings, formatMoney } from "../lib/settings";
import { AlertTriangle, Pencil } from "lucide-react";
import { useAuth } from "../lib/auth";
import { CustomerEditModal } from "../components/CustomerEditModal";
import { CustomerLeads } from "../components/CustomerLeads";

import { tr, lbl } from "../lib/i18n";
export default function CustomerDetailPage() {
  const { id } = useParams();
  const { settings } = useSettings();
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const [reload, setReload] = useState(0);
  const [c, setC] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setError(null);
      try {
        const { data } = await api.get(`/customers/${id}`);
        if (!alive) return;
        setC(data);
        const { data: t } = await api.get(`/customers/${id}/timeline`);
        if (alive) setEvents(t.events || []);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, [id, reload]);

  if (error) {
    return (
      <div className="panel p-6 flex items-start gap-3 border-l-4 border-red-500" data-testid="customer-error">
        <AlertTriangle size={20} className="text-red-500 mt-0.5" />
        <div>
          <div className="font-semibold">{tr("x.could_not_load_customer")}</div>
          <div className="text-xs text-[var(--gray-400)] mt-0.5">{error}</div>
        </div>
      </div>
    );
  }
  if (!c) return <div className="text-center py-10 text-[var(--gray-400)]">{tr("x.loading")}</div>;

  return (
    <div className="space-y-5" data-testid="customer-detail">
      <CustomerLeads customerId={c.id} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">{tr("x.customer")}</div>
          <h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }} data-testid="customer-name">{c.name}</h1>
          <div className="text-sm text-[var(--gray-400)]">{[c.mobile, c.email, lbl(c.customer_type)].filter(Boolean).map((x, i) => <span key={i}>{i ? " · " : ""}<bdi>{x}</bdi></span>)}</div>
        </div>
        {can("customers", "edit") && (
          <button className="btn-secondary" onClick={() => setEditing(true)} data-testid="customer-edit-btn"><Pencil size={14} /> {tr("x.edit")}</button>
        )}
      </div>
      {editing && <CustomerEditModal customer={c} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); setReload((n) => n + 1); }} />}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="panel p-4"><div className="eyebrow mb-2">{tr("x.stage")}</div><div className="font-semibold" data-testid="customer-stage">{lbl(c.stage)}</div></div>
        <div className="panel p-4"><div className="eyebrow mb-2">{tr("x.source")}</div><div className="font-semibold">{lbl(c.source)}</div></div>
        <div className="panel p-4"><div className="eyebrow mb-2">{tr("x.follow_up")}</div><div className="font-semibold">{c.follow_up_date || "—"}</div></div>
      </div>
      <div className="panel p-5">
        <div className="eyebrow mb-3">{tr("x.communication_history")}</div>
        {events.length === 0 && <div className="text-sm text-[var(--gray-400)]">{tr("x.no_events_yet")}</div>}
        <ol className="space-y-3">
          {events.map((e, i) => (
            <li key={i} className="flex gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent-brand mt-2" />
              <div className="flex-1 text-sm">
                <div className="font-semibold">{lbl(e.type)}</div>
                <div className="text-xs text-[var(--gray-400)]"><bdi>{(e.at || "").replace("T", " ").slice(0, 19)}</bdi></div>
                <div className="text-xs mt-1">
                  {e.type === "sale" && `${e.data.reference} — ${formatMoney(e.data.total, settings)}`}
                  {e.type === "reservation" && `${e.data.reference} · ${lbl(e.data.status)}`}
                  {e.type === "transaction" && `${lbl(e.data.type)} ${lbl(e.data.direction)} ${formatMoney(e.data.amount, settings)}`}
                  {(e.type.startsWith("form_")) && `${e.data.reference} · ${lbl(e.data.status)}`}
                  {e.type === "chat" && tr("x.live_chat_messages", { p0: (e.data.transcript || []).length, p1: e.data.vehicle_label ? ` · ${e.data.vehicle_label}` : "" })}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
